import { useEffect, useState } from 'react';
import { Copy, Check, X, AlertTriangle } from 'lucide-react';

// ApiKeyModal  -  masked-stars + click-to-copy per ADR 0008 §5.
// Plaintext is shown ONLY inside the copy gesture; never re-displayed after
// close. Component owns the timer; parent passes the plaintext + onClose.
export default function ApiKeyModal({ name, plaintext, onClose }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(false), 2500);
      return () => clearTimeout(t);
    }
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
    } catch (e) {
      // Clipboard API blocked  -  fall back to selection prompt.
      setRevealed(true);
    }
  }

  if (!plaintext) return null;
  const masked = '••••••••••••••••••••••••••••••••••' + plaintext.slice(-4);

  return (
    <div role="dialog" aria-modal="true" style={overlay}>
      <div style={modal}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 18 }}>API key created</h2>
          <button onClick={onClose} style={closeBtn} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p style={subtitle}>
          <strong>{name}</strong>  -  copy this key now. We will never show it again.
        </p>

        <div style={{ ...warnRow }}>
          <AlertTriangle size={16} color="#ff5f1f" />
          <span>Store this somewhere safe. Closing this modal hides the key forever.</span>
        </div>

        <div style={keyRow}>
          <code style={keyText}>{revealed ? plaintext : masked}</code>
          <button onClick={copy} style={copyBtn}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <button type="button" onClick={onClose} style={doneBtn}>
          I have saved my key
        </button>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.7)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16,
};
const modal = {
  background: 'var(--bg-1, #fff)', color: 'var(--fg-1, #1a1a1a)', borderRadius: 12,
  padding: 28, maxWidth: 520, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
  border: '1px solid var(--line-2, rgba(0,0,0,0.08))',
};
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtn = { background: 'transparent', border: 0, cursor: 'pointer', color: 'inherit', padding: 4 };
const subtitle = { color: 'var(--fg-2, #555)', fontSize: 14, margin: '0 0 16px' };
const warnRow = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 12,
  background: 'rgba(255, 95, 31, 0.08)', borderRadius: 6, color: 'var(--accent, #ff5f1f)',
  fontSize: 13, marginBottom: 16,
};
const keyRow = {
  display: 'flex', gap: 8, alignItems: 'stretch',
  border: '1px solid var(--line-2, rgba(0,0,0,0.12))', borderRadius: 6, overflow: 'hidden',
};
const keyText = {
  flex: 1, padding: '12px 14px', fontFamily: 'var(--font-mono, monospace)', fontSize: 13,
  background: 'var(--bg-0, #f7f7f8)', color: 'var(--fg-0, #0a0a0a)', wordBreak: 'break-all',
};
const copyBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px',
  background: 'var(--accent, #ff5f1f)', color: '#fff', border: 0, cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
const doneBtn = {
  marginTop: 20, width: '100%', padding: '12px 16px', background: 'var(--bg-2, #eee)',
  border: '1px solid var(--line-2, rgba(0,0,0,0.12))', borderRadius: 6, cursor: 'pointer',
  fontSize: 14, fontWeight: 600, color: 'var(--fg-1, #1a1a1a)',
};
